import React, { useEffect, useState } from 'react';
import { Heart } from 'lucide-react';
import { api } from '../lib/api';
import useHomepageVisibility from '../hooks/useHomepageVisibility';
import { useTwitchChannel } from '../hooks/useTwitch';
import './obs/_obsShared.css';

/**
 * Small live heart-rate badge that polls `/api/pulsoid/latest` every 2s.
 *
 * Behavior:
 *  - Renders nothing until we have ever received a bpm (or backend exposes one).
 *  - When `online` is true: full fuchsia/rose glow + pulse synced to BPM.
 *  - When stale/offline but a previous bpm exists: keeps the last bpm
 *    visible in a muted greyscale style with a slow 1.6s idle beat so
 *    the homepage doesn't suddenly lose the badge between sessions.
 */
export default function HeartRateBubble({ size = 58, className = '', style = {} }) {
  const [bpm, setBpm] = useState(null);
  const [online, setOnline] = useState(false);
  const { heart_rate: visible, heart_rate_only_live: onlyLive, loaded } = useHomepageVisibility();
  const { live } = useTwitchChannel();

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get('/pulsoid/latest');
        if (!alive) return;
        if (r.data?.bpm != null) setBpm(r.data.bpm);
        setOnline(Boolean(r.data?.online));
      } catch {
        /* keep last value */
      }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  if (!loaded) return null;
  if (!visible) return null;
  if (onlyLive && !live?.isLive) return null;
  if (!bpm) return null;

  const beatSec = online && bpm > 0 ? 60 / bpm : 1.6;

  const haloBg = online
    ? 'radial-gradient(circle, rgba(244,63,94,0.55) 0%, rgba(232,121,249,0.25) 50%, rgba(0,0,0,0) 75%)'
    : 'radial-gradient(circle, rgba(120,113,108,0.40) 0%, rgba(82,82,91,0.18) 50%, rgba(0,0,0,0) 75%)';

  const bubbleBg = online
    ? 'radial-gradient(circle at 30% 28%, rgba(251,113,133,0.95) 0%, rgba(225,29,72,0.95) 45%, rgba(124,58,237,0.95) 100%)'
    : 'radial-gradient(circle at 30% 28%, rgba(168,162,158,0.85) 0%, rgba(87,83,78,0.90) 45%, rgba(39,39,42,0.95) 100%)';

  const bubbleShadow = online
    ? '0 6px 20px -2px rgba(232,29,72,0.55), inset 0 -4px 10px rgba(0,0,0,0.30), inset 0 3px 8px rgba(255,255,255,0.22)'
    : '0 4px 14px -2px rgba(0,0,0,0.55), inset 0 -4px 10px rgba(0,0,0,0.35), inset 0 3px 8px rgba(255,255,255,0.10)';

  return (
    <div
      data-testid="hero-heart-rate"
      data-online={online ? '1' : '0'}
      className={className}
      style={{ width: size, height: size, ...style }}
      title={online ? `Live hartslag: ${bpm} BPM` : `Laatst gemeten: ${bpm} BPM (offline)`}
    >
      {/* Halo */}
      <div
        style={{
          position: 'absolute',
          inset: -6,
          borderRadius: '50%',
          background: haloBg,
          filter: 'blur(2px)',
          animation: `jdv-heart-halo ${beatSec}s ease-in-out infinite`,
          pointerEvents: 'none',
          opacity: online ? 1 : 0.55,
        }}
      />
      {/* Bubble */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: bubbleBg,
          border: online
            ? '2px solid rgba(255,255,255,0.55)'
            : '2px solid rgba(255,255,255,0.20)',
          boxShadow: bubbleShadow,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: online ? '#fff' : 'rgba(255,255,255,0.78)',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
          transformOrigin: 'center',
          animation: `jdv-heart-pump ${beatSec}s ease-in-out infinite`,
          opacity: online ? 1 : 0.78,
        }}
      >
        <Heart
          fill={online ? '#ffffff' : 'none'}
          strokeWidth={1.5}
          style={{
            width: Math.round(size * 0.30),
            height: Math.round(size * 0.30),
            color: online ? '#fff' : 'rgba(255,255,255,0.65)',
            filter: online
              ? 'drop-shadow(0 0 5px rgba(255,255,255,0.6))'
              : 'none',
            marginBottom: 1,
          }}
        />
        <div
          data-testid="hero-heart-rate-bpm"
          style={{
            fontSize: Math.round(size * 0.28),
            fontWeight: 800,
            lineHeight: 1,
            letterSpacing: '-0.02em',
            textShadow: '0 1px 2px rgba(0,0,0,0.40)',
          }}
        >
          {bpm}
        </div>
      </div>
    </div>
  );
}
